import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { Toggle } from "components/designV2/toggle/toggle";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type { TimeReportsV2Item } from "services/api/company/timeReports.service";
import {
  formatMinutesDuration,
  formatTimeRangeLabel,
  minutesBetweenIsoStrict,
} from "web-apps/company/utils/timeAndDate";
import { useInlineTimeReportEdit } from "web-apps/company/components/timeReportsV2/components/views/person/hooks/useInlineTimeReportEdit";
import type { InlineTimeReportEditActivity } from "web-apps/company/components/timeReportsV2/components/views/person/hooks/useInlineTimeReportEdit";
import { resolveInlineTimeReportPayload } from "web-apps/company/components/timeReportsV2/components/views/person/models/personTimeReport.inlineEdit.logic";
import { resolveStampedTimeRangeValue } from "web-apps/company/components/timeReportsV2/components/views/person/models/personView.stampedTime.mapper";
import type { PersonDetailRowViewModel } from "web-apps/company/components/timeReportsV2/components/views/person/models/personView.types";
import { getTagIconColor } from "./personRow.presentation";
import {
  DetailCheckboxCell,
  DetailDate,
  DetailWeekday,
  IconButton,
  StatusChip,
  StatusChipRow,
  StatusMeta,
} from "./personSummaryRow.styled";
import {
  ColumnLabel,
  ColumnValue,
  CommentCell,
  DateButton,
  DetailColumn,
  DetailedIdentityColumn,
  DetailedRow,
  DetailedStatusColumn,
  DetailedStatusContent,
  InputRow,
  ReportedTimeLabel,
  RetryButton,
  RowFeedback,
} from "./personTimeReportDetailedRow.styled";

interface PersonTimeReportDetailedRowProps {
  readonly companyId?: number;
  readonly detailRow: PersonDetailRowViewModel;
  readonly hasDeviation: boolean;
  readonly isDetailOpeningDisabled?: boolean;
  readonly isReadOnly: boolean;
  readonly isSelected: boolean;
  readonly item: TimeReportsV2Item;
  readonly updatedAtLabel: string;
  readonly onOpenDetails: () => void;
  readonly onPendingChange?: (activity: InlineTimeReportEditActivity) => void;
  readonly onSaved: () => void;
  readonly onToggleSelection: (checked: boolean) => void;
}

export const PersonTimeReportDetailedRow = memo(
  (props: PersonTimeReportDetailedRowProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const edit = useInlineTimeReportEdit({
      companyId: props.companyId,
      item: props.item,
      isReadOnly: props.isReadOnly,
      onSaved: props.onSaved,
      onPendingChange: (hasPendingChanges): void =>
        props.onPendingChange?.({
          hasPendingChanges,
          timeReportId: props.item.id,
        }),
    });
    const workerComment = props.item.userNotes;
    const stampedRange = resolveStampedTimeRangeValue(
      props.item.stampedStartTime,
      props.item.stampedEndTime,
      i18n.language,
    );
    const payload = resolveInlineTimeReportPayload({
      item: props.item,
      draft: edit.draft,
    });
    const grossMinutes = payload.ok
      ? minutesBetweenIsoStrict(payload.value.startTime, payload.value.endTime)
      : null;
    const breakMinutes = payload.ok
      ? payload.value.breakStartTime === payload.value.breakEndTime
        ? 0
        : minutesBetweenIsoStrict(
            payload.value.breakStartTime,
            payload.value.breakEndTime,
          )
      : null;
    const total =
      grossMinutes != null && breakMinutes != null
        ? formatMinutesDuration(grossMinutes - breakMinutes)
        : "–";
    const save = (): void => {
      void edit.save();
    };
    const validationKey =
      edit.validationError === "incomplete-times"
        ? "TimeReports.EnterReportedTimes"
        : "TimeReports.InlineInvalidTimes";
    const tags = [props.detailRow.deviationTag, props.detailRow.statusTag];

    return (
      <DetailedRow
        $hasDeviation={props.hasDeviation}
        aria-label={props.detailRow.dateLabel}
        aria-busy={edit.isSaving}
      >
        <DetailedIdentityColumn>
          <DetailCheckboxCell>
            <Checkbox
              checked={props.isSelected ? "checked" : "unchecked"}
              onChange={props.onToggleSelection}
            />
          </DetailCheckboxCell>
          <DateButton
            type="button"
            disabled={edit.isSaving || props.isDetailOpeningDisabled}
            onClick={props.onOpenDetails}
            aria-label={`${t("TimeReports.TimeReport")} ${props.detailRow.dateLabel}`}
          >
            <DetailDate>{props.detailRow.dateLabel}</DetailDate>
            <DetailWeekday>{props.detailRow.weekdayLabel}</DetailWeekday>
          </DateButton>
        </DetailedIdentityColumn>
        <DetailColumn>
          <ColumnValue>
            {formatTimeRangeLabel(
              props.item.plannedStartTime,
              props.item.plannedEndTime,
              i18n.language,
            )}
          </ColumnValue>
          <ColumnLabel>{t("TimeReports.PlannedTime")}</ColumnLabel>
        </DetailColumn>
        <DetailColumn>
          <ColumnValue>{stampedRange ?? "-"}</ColumnValue>
          <ColumnLabel>{t("TimeReports.StampedTime")}</ColumnLabel>
        </DetailColumn>
        <DetailColumn>
          <InputRow>
            <TimePicker
              size="compact"
              placeholder="--:--"
              value={edit.draft.start}
              disabled={edit.isDisabled}
              openOnFocus={false}
              ariaLabel={t("TimeReports.ReportedStart")}
              onChange={(value) => edit.setField({ field: "start", value })}
              onCommit={save}
              onBlur={save}
            />
            <ColumnValue>–</ColumnValue>
            <TimePicker
              size="compact"
              placeholder="--:--"
              value={edit.draft.end}
              disabled={edit.isDisabled}
              openOnFocus={false}
              ariaLabel={t("TimeReports.ReportedEnd")}
              onChange={(value) => edit.setField({ field: "end", value })}
              onCommit={save}
              onBlur={save}
            />
          </InputRow>
          <ReportedTimeLabel>
            {t("TimeReports.ReportedTimeSection")}
            {edit.isSaving && (
              <span role="status">{t("TimeReports.Saving")}</span>
            )}
          </ReportedTimeLabel>
          {edit.validationError && (
            <RowFeedback $hasError role="alert">
              {t(validationKey)}
            </RowFeedback>
          )}
          {edit.hasSaveError && (
            <RowFeedback $hasError role="alert">
              {t("TimeReports.InlineSaveError")}{" "}
              <RetryButton type="button" onClick={save}>
                {t("TimeReports.Retry")}
              </RetryButton>
            </RowFeedback>
          )}
        </DetailColumn>
        <DetailColumn>
          <ColumnValue>{total}</ColumnValue>
          <ColumnLabel>{t("TimeReports.TotalTime")}</ColumnLabel>
        </DetailColumn>
        <DetailColumn>
          <InputRow>
            <ColumnValue>{edit.draft.breakMinutes}m</ColumnValue>
            <Toggle
              size="compact"
              checked={edit.draft.breakEnabled}
              disabled={edit.isDisabled || Number(edit.draft.breakMinutes) <= 0}
              aria-label={t("TimeReports.BreakEnabled")}
              onChange={edit.toggleBreak}
            />
          </InputRow>
          <ColumnLabel>{t("TimeReports.UnpaidBreak")}</ColumnLabel>
        </DetailColumn>
        <DetailedStatusColumn>
          {workerComment !== null && (
            <CommentCell>
              <TooltipHover
                text={workerComment}
                textAlign="left"
                maxWidth={360}
              >
                <IconButton
                  type="button"
                  aria-label={t("TimeReports.WorkerComment")}
                >
                  <CustomIcon
                    name="v2/chat"
                    size="22px"
                    color={ColorV2.Blue4}
                  />
                </IconButton>
              </TooltipHover>
            </CommentCell>
          )}
          <DetailedStatusContent>
            <StatusChipRow>
              {tags.map(
                (tag) =>
                  tag && (
                    <StatusChip
                      key={`${tag.tone}-${tag.text}`}
                      $tone={tag.tone}
                    >
                      <CustomIcon
                        name={tag.iconName}
                        size="17px"
                        color={getTagIconColor(tag.tone)}
                      />
                      {tag.text}
                    </StatusChip>
                  ),
              )}
            </StatusChipRow>
            <StatusMeta>{props.updatedAtLabel}</StatusMeta>
          </DetailedStatusContent>
        </DetailedStatusColumn>
        <IconButton
          type="button"
          disabled={edit.isSaving || props.isDetailOpeningDisabled}
          onClick={props.onOpenDetails}
          aria-label={`${t("TimeReports.TimeReport")} ${props.detailRow.dateLabel}`}
        >
          <CustomIcon
            name="v2/right-chevron"
            size="20px"
            color={ColorV2.Blue4}
          />
        </IconButton>
      </DetailedRow>
    );
  },
);
